import React, { useEffect, useMemo, useState } from 'react';
import { addDays, fmtHM, fmtISO, fmtLong } from '../lib/format.js';
import { Icon } from './Icon.jsx';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function sourceDate(targetDate, weeksBack) {
  return addDays(targetDate, -7 * weeksBack);
}

/**
 * Default the source-week picker to the most recent week (1..4 back) that
 * actually has entries for the same weekday. If none of the four candidate
 * dates have any entries, fall back to 1 (last <weekday>) — the user can
 * still pick a custom date or another source.
 */
export function pickDefaultSourceWeeks(targetDate, allEntries) {
  for (let w = 1; w <= 4; w++) {
    const iso = fmtISO(sourceDate(targetDate, w));
    if (allEntries.some((e) => !e.deleted && e.date === iso)) return w;
  }
  return 1;
}

/**
 * Clone the picked source entries onto `targetDate`. Each clone gets the
 * canonical 'copy' source and a back-pointer (copiedFromId/copiedFromDate)
 * so analytics and a future "undo all copies from <date>" feature have a
 * trail. The ticket/epic split and other metadata are passed through
 * unchanged.
 */
export function buildCopyPayloads(targetDate, pickedEntries) {
  const dateISO = fmtISO(targetDate);
  return pickedEntries.map((e) => ({
    date: dateISO,
    activityName: e.activityName,
    product: e.product || '',
    clientName: e.clientName || '',
    ticketId: e.ticketId || '',
    ticketTitle: e.ticketTitle || '',
    epicId: e.epicId || '',
    epicTitle: e.epicTitle || '',
    hours: e.hours,
    minutes: e.minutes,
    description: e.description || '',
    startTime: e.startTime,
    source: 'copy',
    copiedFromId: e.id,
    copiedFromDate: e.date,
  }));
}

function chipLabel(weeksBack) {
  if (weeksBack === 1) return 'LAST ';
  return `${weeksBack} WEEKS AGO`;
}

function SourceChip({ weeksBack, targetDate, allEntries, active, onSelect }) {
  const src = sourceDate(targetDate, weeksBack);
  const iso = fmtISO(src);
  const dayName = DAY_NAMES[src.getDay()];
  const label = weeksBack === 1 ? `LAST ${dayName.toUpperCase()}` : `${weeksBack} WEEKS AGO`;
  const entries = allEntries.filter((e) => !e.deleted && e.date === iso);
  const totalMins = entries.reduce((s, e) => s + e.hours * 60 + e.minutes, 0);
  const hasEntries = entries.length > 0;

  const dateStr = src.toLocaleDateString([], { month: 'short', day: 'numeric' });

  return (
    <button
      type="button"
      onClick={() => hasEntries && onSelect(weeksBack)}
      disabled={!hasEntries}
      style={{
        flex: '1 0 140px',
        padding: '10px 12px',
        borderRadius: 10,
        border: `1px solid ${active ? 'var(--sp-fg-1)' : 'var(--sp-border)'}`,
        background: active ? '#fff' : 'var(--sp-bg-page)',
        boxShadow: active ? '0 0 0 3px rgba(25,25,25,0.08)' : 'none',
        cursor: hasEntries ? 'pointer' : 'not-allowed',
        opacity: hasEntries ? 1 : 0.45,
        textAlign: 'left',
        transition: 'border-color .12s, box-shadow .12s, background .12s',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--sp-fg-3)', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sp-fg-1)', marginBottom: 4 }}>
        {dateStr}
      </div>
      {hasEntries ? (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#16A249', display: 'inline-block' }} />
          <span style={{ color: 'var(--sp-fg-2)' }}>{fmtHM(totalMins)} logged</span>
        </div>
      ) : (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--sp-fg-4)', display: 'inline-block' }} />
          <span style={{ color: 'var(--sp-fg-3)' }}>Nothing logged</span>
        </div>
      )}
    </button>
  );
}

function CopyEntryRow({ entry, checked, onChange }) {
  const mins = entry.hours * 60 + entry.minutes;
  const viaLabel = (() => {
    const s = String(entry.source || '').toLowerCase();
    if (s.startsWith('jira:')) return 'VIA JIRA';
    if (s.startsWith('calendar:')) return 'VIA CALENDAR';
    if (s === 'copy') return 'VIA COPY';
    return null;
  })();

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '24px 76px 1fr auto',
        gap: 14,
        alignItems: 'center',
        padding: '12px 14px',
        borderRadius: 10,
        border: `1px solid ${checked ? 'var(--sp-border)' : 'transparent'}`,
        background: checked ? '#fff' : 'var(--sp-bg-page)',
        transition: 'background .1s',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 16, height: 16, accentColor: 'var(--sp-fg-1)', cursor: 'pointer' }}
      />
      <div style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'var(--sp-fg-1)' }}>
        {fmtHM(mins)}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--sp-fg-1)' }}>
          {entry.activityName}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 3 }}>
          {entry.product && entry.product !== 'None' && (
            <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 10, background: entry.product === 'SwiftPay' ? '#E6FDF9' : '#FFE3D3', color: entry.product === 'SwiftPay' ? '#007C7C' : '#B57651' }}>
              {entry.product}
            </span>
          )}
          {entry.ticketId && (
            <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: '#F2EAFE', color: '#5E2A9C', fontFamily: 'DM Sans' }}>
              {entry.ticketId}
            </span>
          )}
          {entry.epicId && (
            <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 4, background: 'var(--sp-bg-muted)', color: 'var(--sp-fg-2)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              {entry.ticketId && <Icon name="flow" size={9} stroke={2} />}
              {entry.epicId}
            </span>
          )}
          {entry.clientName && (
            <span style={{ fontSize: 11, color: 'var(--sp-fg-3)' }}>· {entry.clientName}</span>
          )}
        </div>
      </div>
      {viaLabel && (
        <div style={{ fontSize: 11, color: 'var(--sp-fg-4)', letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
          {viaLabel}
        </div>
      )}
    </div>
  );
}

/**
 * Full-screen overlay for copying a previous week's entries onto the
 * currently-selected day. Change 5 of v2.1.
 *
 * Props:
 *   open         — boolean
 *   onClose      — () => void
 *   targetDate   — Date — the day entries will be copied onto
 *   allEntries   — Entry[] — all loaded entries (the date-bar window) for source-chip lookup
 *   onCopy       — (entries: Partial<Entry>[]) => Promise<void>
 */
export function CopyDayImport({ open, onClose, targetDate, allEntries, onCopy }) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // Default to the most-recent week that has entries, fall back to 1.
  const defaultWeeks = useMemo(() => {
    if (!open) return 1;
    return pickDefaultSourceWeeks(targetDate, allEntries);
  }, [open, targetDate, allEntries]);

  const [sourceWeeks, setSourceWeeks] = useState(defaultWeeks);
  const [showCustom, setShowCustom] = useState(false);
  const [customISO, setCustomISO] = useState('');
  const [picked, setPicked] = useState({});
  const [copying, setCopying] = useState(false);

  // Reset when opened
  useEffect(() => {
    if (!open) return;
    setSourceWeeks(defaultWeeks);
    setShowCustom(false);
    setCustomISO('');
    setCopying(false);
  }, [open, defaultWeeks]);

  const sourceISO = customISO || fmtISO(sourceDate(targetDate, sourceWeeks));

  const sourceEntries = useMemo(
    () => allEntries.filter((e) => !e.deleted && e.date === sourceISO),
    [allEntries, sourceISO],
  );

  // Reset selection when source changes — all pre-selected.
  useEffect(() => {
    const next = {};
    sourceEntries.forEach((e) => { next[e.id] = true; });
    setPicked(next);
  }, [sourceISO]);

  if (!open) return null;

  const pickedEntries = sourceEntries.filter((e) => picked[e.id]);
  const allChecked = sourceEntries.length > 0 && pickedEntries.length === sourceEntries.length;
  const someChecked = pickedEntries.length > 0 && pickedEntries.length < sourceEntries.length;
  const totalPickedMins = pickedEntries.reduce((s, e) => s + e.hours * 60 + e.minutes, 0);

  const sourceDateObj = customISO
    ? (() => { const [y, m, d] = customISO.split('-').map(Number); return new Date(y, m - 1, d); })()
    : sourceDate(targetDate, sourceWeeks);

  const targetDateLabel = fmtLong(targetDate);
  const sourceDateLabel = fmtLong(sourceDateObj);

  const minDateISO = fmtISO(addDays(today, -84));
  const maxDateISO = fmtISO(today);

  const handleSelectSource = (weeks) => {
    setSourceWeeks(weeks);
    setCustomISO('');
  };

  const handleCustomDate = (e) => {
    const iso = e.target.value;
    if (!iso) return;
    const [y, m, d] = iso.split('-').map(Number);
    const chosen = new Date(y, m - 1, d);
    const chosenISO = fmtISO(chosen);
    if (chosenISO === sourceISO) return; // no-op
    setCustomISO(chosenISO);
    setShowCustom(true);
  };

  const handleSelectAll = (e) => {
    const next = {};
    if (e.target.checked) {
      sourceEntries.forEach((en) => { next[en.id] = true; });
    }
    setPicked(next);
  };

  const handleCopy = async () => {
    if (pickedEntries.length === 0 || copying) return;
    setCopying(true);
    const copies = buildCopyPayloads(targetDate, pickedEntries);
    try {
      await onCopy(copies);
    } finally {
      setCopying(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'var(--sp-bg-page)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'tt-fade-in 200ms ease',
      }}
      data-screen-label="Copy day from a previous week"
    >
      {/* Header */}
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '16px 28px',
          borderBottom: '1px solid var(--sp-border)',
          background: '#fff',
          flex: 'none',
        }}
      >
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              background: 'var(--sp-mint-100)',
              color: '#007C7C',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="repeat" size={16} />
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--sp-fg-3)' }}>
              Copy day from a previous week
            </span>
            <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--sp-fg-1)' }}>
              Onto <strong>{targetDateLabel}</strong>
            </span>
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn btn-ghost" onClick={onClose}>
          <Icon name="x" size={14} /> Close
        </button>
      </header>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px 28px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 28 }}>

          {/* Section 1: Copy from */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--sp-fg-3)', marginBottom: 12 }}>
              Copy from
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
              {[1, 2, 3, 4].map((w) => (
                <SourceChip
                  key={w}
                  weeksBack={w}
                  targetDate={targetDate}
                  allEntries={allEntries}
                  active={!customISO && sourceWeeks === w}
                  onSelect={handleSelectSource}
                />
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                type="button"
                onClick={() => setShowCustom((v) => !v)}
                className="btn btn-ghost"
                style={{ fontSize: 12, color: 'var(--sp-fg-2)', padding: '4px 0' }}
              >
                <Icon name="cal" size={12} /> Pick a different day
              </button>
              {showCustom && (
                <input
                  type="date"
                  min={minDateISO}
                  max={maxDateISO}
                  value={customISO}
                  onChange={handleCustomDate}
                  style={{
                    padding: '4px 10px',
                    border: '1px solid var(--sp-border)',
                    borderRadius: 8,
                    fontSize: 13,
                    background: '#fff',
                    color: 'var(--sp-fg-1)',
                  }}
                />
              )}
            </div>
          </div>

          {/* Section 2: Source entries */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sp-fg-1)' }}>
                <strong>{sourceDateLabel}</strong>
                {sourceEntries.length > 0 && (
                  <span style={{ fontWeight: 400, color: 'var(--sp-fg-3)', marginLeft: 8 }}>
                    · {fmtHM(sourceEntries.reduce((s, e) => s + e.hours * 60 + e.minutes, 0))} across {sourceEntries.length} {sourceEntries.length === 1 ? 'entry' : 'entries'}
                  </span>
                )}
              </div>
              {sourceEntries.length > 0 && (
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--sp-fg-2)', cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked; }}
                    onChange={handleSelectAll}
                    style={{ width: 15, height: 15, accentColor: 'var(--sp-fg-1)', cursor: 'pointer' }}
                  />
                  Select all
                </label>
              )}
            </div>

            {sourceEntries.length === 0 ? (
              <div
                className="card"
                style={{ padding: 32, borderRadius: 14, textAlign: 'center' }}
              >
                <div
                  style={{
                    display: 'inline-flex',
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: 'var(--sp-bg-muted)',
                    color: 'var(--sp-fg-3)',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 12,
                  }}
                >
                  <Icon name="clock" size={20} stroke={1.8} />
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>
                  Nothing logged on this day.
                </div>
                <div style={{ fontSize: 13, color: 'var(--sp-fg-3)' }}>
                  Pick another source day from above, or import from Calendar or Jira instead.
                </div>
              </div>
            ) : (
              <div className="card" style={{ padding: 6, borderRadius: 14, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {sourceEntries.map((e) => (
                  <CopyEntryRow
                    key={e.id}
                    entry={e}
                    checked={!!picked[e.id]}
                    onChange={(val) => setPicked((p) => ({ ...p, [e.id]: val }))}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer
        style={{
          padding: '14px 28px',
          borderTop: '1px solid var(--sp-border)',
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flex: 'none',
        }}
      >
        <div style={{ flex: 1, fontSize: 13, color: 'var(--sp-fg-2)' }}>
          {pickedEntries.length === 0 ? (
            <span style={{ color: 'var(--sp-fg-3)' }}>Nothing selected to copy yet</span>
          ) : (
            <span>
              Copying <strong>{pickedEntries.length}</strong> {pickedEntries.length === 1 ? 'entry' : 'entries'} ·{' '}
              <strong>{fmtHM(totalPickedMins)}</strong> total onto <strong>{targetDateLabel}</strong>
            </span>
          )}
        </div>
        <button className="btn btn-ghost" onClick={onClose} disabled={copying}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleCopy}
          disabled={pickedEntries.length === 0 || copying}
          style={{ whiteSpace: 'nowrap' }}
        >
          {copying && <span className="tt-spinner sm" style={{ marginRight: 6 }} />}
          {copying ? 'Copying…' : (
            <>
              <Icon name="repeat" size={14} />
              &nbsp;Copy {pickedEntries.length || 0} {pickedEntries.length === 1 ? 'entry' : 'entries'}
            </>
          )}
        </button>
      </footer>
    </div>
  );
}
